import React from 'react';
import { useDispatch } from 'react-redux';
import { defineMessages } from 'react-intl';
import HfReactHelper from 'common/utils/hf-react-helper';
import { NotificationBannerType } from 'hellospa/components/notification-banner/data/types';
import { externalFile, createBannerMessage } from 'hellospa/page/prep-and-send/data/actions';
import {
  GoogleDriveIntegration, UploadIntegrationType,
} from 'hellospa/page/prep-and-send/data/types/integration';
import ReAuthModal from './reauth-modal';
import googleDriveIcon from '../assets/google_drive_logo.png';

type Gapi = {
  load: (type: string) => {},
  auth: any,
}

type Google = {
  picker: any,
}

declare const google: Google;
declare global {
  interface Window { gapi: Gapi }
}

type DriveDoc = {
  id: string,
  name: string,
}

type DriveActionType = {
  action: 'picked' | 'cancel',
  docs: DriveDoc[],
}

type DriveAuthResultType = {
  error?: string,
  // eslint-disable-next-line camelcase
  error_subtype?: string,
  // eslint-disable-next-line camelcase
  access_token?: string,
  details?: string,
}

type Props = {
  settings: GoogleDriveIntegration,
  shouldTriggerPicker: boolean,
  toggleIntegrationPicker: (integration: string, status: boolean) => void,
}

const GOOGLE_API_URL = 'https://apis.google.com/js/api.js';
const SCOPE = ['https://www.googleapis.com/auth/drive.readonly'];
const MIME_TYPES = 'image/pjpeg,image/tiff,application/html,image/x-tiff,application/vnd,text/xml,application/zip,application/xml,image/jpeg,image/png,text/plain,text/rtf,image/bmp,image/gif,application/msword,text/html,application/pdf,application/xys,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.openxmlformats-officedocument.presentationml.presentation,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.google-apps.document,application/vnd.google-apps.presentation,application/vnd.google-apps.spreadsheet,application/vnd.google-apps.drawing,application/vnd.google-apps.kix,application/vnd.google-apps.punch';

// messages are from the backend specific to Google Drive
const messages = defineMessages({
  defaultError: {
    id: '',
    description: 'Error message showing that a google drive connection not established',
    defaultMessage: 'Unable to obtain access to your Google Drive account. Please try again or contact us at {email}',
  },
  immediateFailed: {
    id: '',
    description: 'Error message showing that google drive integration needs to be reconnected',
    defaultMessage: 'Please remove your existing Google integration in your settings before trying to connect a new one.',
  },
  unknownError: {
    id: '',
    description: 'Error message for an unknown error',
    defaultMessage: 'Sorry something went wrong, please try again later. Error: {error}',
  },
});

/**
 * Returns whether the Google API script is loaded
 * @return {boolean}
 */
function isGoogleApiLoaded() {
  return window.gapi != null;
}

/**
 * Load the script to inject into the DOM (happens on component mount)
 * @param  {GoogleDriveIntegration} settings
 * @return {void}
 */
function initialize(settings: GoogleDriveIntegration) {
  if (settings.token != null) {
    const script = document.createElement('script');
    script.src = GOOGLE_API_URL;
    script.async = true;

    return script;
  }

  return null;
}

/**
 * Load Google API related stuff
 * @return {void}
 */
function loadPicker() {
  if (isGoogleApiLoaded()) {
    window.gapi.load('auth');
    window.gapi.load('picker');
  }
}

/**
 * Sets up the file picker and opens it
 * @param  {string} accessToken
 * @param  {GoogleDriveIntegration} settings
 * @param  {(data: DriveActionType)} onAction
 * @return {void}
 */
function triggerDrivePicker(
  accessToken: string,
  settings: GoogleDriveIntegration,
  onAction: (data: DriveActionType) => void,
) {
  if (isGoogleApiLoaded() && accessToken) {
    const view = new google.picker.View(google.picker.ViewId.DOCS).setMimeTypes(MIME_TYPES);
    const picker = new google.picker.PickerBuilder()
      .setAppId(settings.abbrKey)
      .setOrigin(window.location.protocol + HfReactHelper.urlHelper(''))
      .setOAuthToken(accessToken) // should this be settings.token?
      .addView(view)
      .enableFeature(google.picker.Feature.NAV_HIDDEN)
      .enableFeature(google.picker.Feature.MINE_ONLY)
      .enableFeature(google.picker.Feature.SUPPORT_DRIVES)
      .setCallback(onAction)
      .build();
    // show the GoogleDrive picker
    picker.setVisible(true);
  }
}

export default function GoogleDriveFilePicker({
  settings,
  shouldTriggerPicker,
  toggleIntegrationPicker,
}: Props) {
  const dispatch = useDispatch();

  React.useEffect(() => {
    const script = initialize(settings);
    if (script != null) {
      document.body.appendChild(script);
      // adding a callback to initialize auth & picker when the script loads
      script.addEventListener('load', loadPicker);
    }

    return () => {
      if (script != null) {
        script.removeEventListener('load', loadPicker);
        document.body.removeChild(script);
      }
    };
  }, [settings]);

  const onAction = React.useCallback(async (data: DriveActionType) => {
    switch (data.action) {
      case google.picker.Action.PICKED: {
        // select the first value since multi select is disabled currently
        const file = data.docs.length > 0 ? data.docs[0] : null;
        if (file) {
          dispatch(externalFile(UploadIntegrationType.GoogleDrive, file.id, file.name));
        }
        toggleIntegrationPicker(UploadIntegrationType.GoogleDrive, false);
        return;
      }
      // clicking the close button on the file picker window,
      // setting the default case to close, for eslint
      case google.picker.Action.CANCEL:
      default:
        toggleIntegrationPicker(UploadIntegrationType.GoogleDrive, false);
    }
  }, [toggleIntegrationPicker, dispatch]);

  React.useEffect(() => {
    // trigger picker here, also check if it's integrated already
    if (shouldTriggerPicker && settings.enabled && isGoogleApiLoaded()) {
      window.gapi.auth.authorize({
        client_id: settings.key,
        scope: SCOPE,
        // will require the user to force select an account so safari is happy
        // chrome will just continue on if an account is already selected
        immediate: false,
      }, (result: DriveAuthResultType) => {
        if (result && result.error) {
          if (result.error === 'immediate_failed' && result.error_subtype === 'access_denied') {
            dispatch(createBannerMessage(messages.immediateFailed, NotificationBannerType.Err));
          } else {
            // use details if no subtype
            const errorSubtype = result.error_subtype || result.details;
            const error = `${result.error} (${errorSubtype})`;
            dispatch(createBannerMessage(
              messages.unknownError,
              NotificationBannerType.Err,
              { error },
            ));
          }
          toggleIntegrationPicker(UploadIntegrationType.GoogleDrive, false);
          return;
        }

        if (result && result.access_token != null) {
          triggerDrivePicker(result.access_token, settings, onAction);
          return;
        }

        const email = HfReactHelper.isHelloSign() ? 'support@hellosign.com' : 'support@hellofax.com';
        // default error if everything fails
        dispatch(createBannerMessage(
          messages.defaultError,
          NotificationBannerType.Err,
          { email },
        ));
        toggleIntegrationPicker(UploadIntegrationType.GoogleDrive, false);
      });
    }
  }, [dispatch, onAction, settings, shouldTriggerPicker, toggleIntegrationPicker]);

  // if clicked on the integration icon but it's not enabled,
  // trigger the modal to initiate auth
  if (shouldTriggerPicker && !settings.enabled) {
    return (
      <ReAuthModal
        integration={UploadIntegrationType.GoogleDrive}
        integrationIcon={googleDriveIcon}
        isOpen={shouldTriggerPicker && !settings.enabled}
        onClose={() => toggleIntegrationPicker(UploadIntegrationType.GoogleDrive, false)} />
    );
  }

  return null;
}
